import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { and } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import UiButton from 'garaje/components/ui-button';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';
import isLastIndex from 'garaje/utils/is-last-index';
import _uniqBy from 'lodash/uniqBy';

interface MultipleScreenersInfoSignature {
  Args: {
    results: ScreenerResult[];
    model: GuestWithVisitApproval;
    toggleModal: (result: ScreenerResult) => void;
  };
}

export default class MultipleScreenersInfo extends Component<MultipleScreenersInfoSignature> {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  get uniqueResults(): ScreenerResult[] {
    return _uniqBy(this.args.results, (result) => {
      return getScreenerDisplayName(result.screenerSource);
    });
  }

  @action
  canOpenUnifiedScreenersModal(result: ScreenerResult): boolean {
    if (!result.details) return false;

    const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;
    const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);
    const canView = this.abilities.can(`view ${source}`, this.#permissionsModel);

    return canReview || canView;
  }

  <template>
    <div class="mt-1 inline-table text-sm" data-test-multiple-approvals-screeners>
      {{#each this.uniqueResults as |result index|}}
        {{~#if (and result.details (this.canOpenUnifiedScreenersModal result))}}
          <UiButton
            class="btn btn--link btn--small !text-gem-50"
            {{on "click" (fn @toggleModal result)}}
            data-test-screener-result-detail-trigger
          >
            {{getScreenerDisplayName result.screenerSource}}
          </UiButton>
        {{~else}}
          {{getScreenerDisplayName result.screenerSource}}
        {{~/if}}{{#unless (isLastIndex index this.uniqueResults)}},{{/unless}}
      {{/each}}
    </div>
  </template>
}
